/**
 * Renderer-side fallback parser for AskUserQuestion drift (#215).
 *
 * The `/build-game` interview SKILL is supposed to issue every
 * question as an `AskUserQuestion` tool call so the renderer can
 * paint a structured option-button card (`AskUserQuestionCard` in
 * `pages/Workspace.tsx`). The model holds that contract for the first
 * 3-5 turns then drifts and starts emitting plain markdown of the
 * shape:
 *
 *   Question 5 of ~12: Which palette?
 *
 *   (a) Bright primary colours
 *   (b) Pastel / soft
 *   (c) Monochrome
 *   (d) Neon / saturated
 *
 * Issue #215 + hello-weekend PR #72 establish that the SKILL-side
 * MANDATORY rule alone doesn't reliably hold — model behaviour drift,
 * not a prompt deficiency. This module is the renderer-side fallback:
 * detect that exact shape, expose it to the bubble component, which
 * paints a clickable button strip below the rendered markdown.
 *
 * Conservatism:
 *
 *   This parser MUST NOT false-positive on regular markdown. A
 *   regular bullet list (`- foo`) or a plain paragraph that happens
 *   to mention the word "question" must not trigger the strip — that
 *   would be a worse experience than the bug we're fixing.
 *
 *   We require the LITERAL `Question N of M:` prefix (case-sensitive)
 *   anchored at the start of a line. The `Question` word, the digits,
 *   the literal " of ", another digit run (with optional `~` for
 *   "approximately"), the colon, the question text — all mandatory.
 *   Without that prefix the parser returns null and the renderer falls
 *   back to plain markdown, exactly as today.
 */

/**
 * Parsed shape of a markdown-emitted question. Mirrors the relevant
 * subset of the structured `AskUserQuestionCard` props so the renderer
 * can paint the same button strip from either source.
 */
export interface ParsedMarkdownQuestion {
  /** The text after `Question N of M:` on the prefix line. */
  readonly question: string;
  /** Option labels in the order the model emitted them, no letter
   *  prefix. The renderer dispatches the label text verbatim as the
   *  user's answer when an option is clicked. */
  readonly options: readonly string[];
}

/**
 * Anchored to the start of a line. Captures:
 *   1. question number (digits)
 *   2. total questions (digits, optionally prefixed `~`)
 *   3. the question text up to end-of-line
 *
 * Multiline + dotall not needed — the `m` flag is enough to anchor
 * `^` to a line start, and `[^\n]+` keeps us on the same line for the
 * question text.
 */
// Bugbot fix: use `[^\S\n]` (horizontal whitespace only) instead of `\s`
// so the prefix can't span lines. Plain `\s` matches newlines, which
// would let "Question" at the end of one line and "5 of M: text" on the
// next bogus-match.
const QUESTION_PREFIX = /^Question[^\S\n]+(\d+)[^\S\n]+of[^\S\n]+~?\d+[^\S\n]*:[^\S\n]*([^\n]+)$/m;

/**
 * Strip simple inline `**bold**` markdown wrappers from a single line so
 * the model's bolded variants of both the prefix and individual option
 * labels parse the same as their plain forms (#226).
 *
 * The model frequently emits the drifted shape with the prefix wrapped
 * (`**Question 5 of ~12:**`) and/or option labels wrapped
 * (`(a) **Time pressure** — racing...`). Without this preprocessing the
 * prefix regex's `^Question` anchor fails on the leading `**`, and even
 * when the prefix matches, option button labels render with literal
 * asterisks visible to the user.
 *
 * Conservatism — match real markdown emphasis only:
 *   - Opener `**` MUST be immediately followed by a NON-whitespace
 *     character; closer `**` MUST be immediately preceded by a
 *     NON-whitespace character. This mirrors CommonMark's emphasis
 *     rule and means literal double-asterisk in math/code text like
 *     `2 ** 3 ** 2` (exponent notation, with surrounding spaces) is
 *     left untouched. Codex correctness review on PR #228 flagged
 *     the unconditional strip as a regression risk for technical
 *     prompts.
 *   - Bold spans MUST be on a single line: inner content rejects
 *     newlines via `[^*\n]`. We do NOT strip multi-line bold spans
 *     because in regular prose a stray `**` opener might never close
 *     on the same line and we'd corrupt unrelated content. Single-line
 *     spans are what the model emits for both prefix labels and
 *     option labels.
 *   - We also do NOT strip italic `*foo*` or `_foo_` — the parser
 *     currently has no false-positive driver for those and a
 *     conservative scope reduces collateral damage on unrelated
 *     markdown.
 *
 * The `\S[^*\n]*?\S|\S` alternation handles both single-character
 * spans (`**a**`) and multi-character spans (`**foo bar**`), in both
 * cases requiring non-whitespace at each end. The single-char branch
 * can never have whitespace because `\S` already excludes it.
 */
const BOLD_INLINE = /\*\*(\S[^*\n]*?\S|\S)\*\*/g;

/**
 * Match a single option line. The model emits options as either
 * `(a) text` or `a) text` — we accept both. Letters a-h are plenty
 * for any AskUserQuestion-style prompt; capping at h means a stray
 * `i.` Roman-numeral or list bullet doesn't false-match.
 */
const OPTION_LINE = /^\s*\(?([a-h])\)\s+(.+?)\s*$/i;

/**
 * Detect the AskUserQuestion drift shape and return the question +
 * options, or null if the text is anything else (including a regular
 * markdown bullet list, a paragraph that just mentions the word
 * "question", a code block, etc.).
 *
 * Rules:
 *   - The literal `Question N of M:` prefix MUST appear at the start
 *     of a line.
 *   - At least two `(a)` / `(b)` / ... lines MUST follow it (single-
 *     option "questions" don't justify a button strip).
 *   - Option letters MUST start at `(a)` and increment by one without
 *     gaps. `(a)`, `(b)`, `(c)` is valid; `(a)`, `(c)` is not — that
 *     pattern is much more likely to be e.g. citations than an
 *     options list.
 *   - Option lines MUST be contiguous (no blank or non-option lines
 *     between them). Lets us cleanly reject prose that happens to
 *     contain `(a) something` as part of a sentence.
 *   - Code-fenced content is ignored (a triple-backtick block can
 *     legitimately contain `(a)` lines that aren't an options strip).
 */
export function parseMarkdownQuestion(text: string): ParsedMarkdownQuestion | null {
  if (!text) return null;

  // Strip out any triple-backtick fenced content so a code block
  // can't masquerade as a question. Replacing with newlines keeps
  // line numbering roughly aligned for any future error messages.
  const codeFenceMasked = text.replace(/```[\s\S]*?```/g, (m) => m.replace(/[^\n]/g, ' '));

  // Then strip simple `**bold**` wrappers (#226). Must run AFTER
  // code-fence masking so a `**` inside a fenced block can't reach
  // here and confuse the prefix regex on a neighbouring line. Only
  // touches single-line spans — see BOLD_INLINE for the rationale.
  const stripped = codeFenceMasked.replace(BOLD_INLINE, '$1');

  const prefixMatch = QUESTION_PREFIX.exec(stripped);
  if (!prefixMatch) return null;

  const questionText = prefixMatch[2]?.trim();
  if (!questionText) return null;

  // Slice everything after the prefix line and split into lines so we
  // can walk them in order.
  const afterPrefix = stripped.slice((prefixMatch.index ?? 0) + prefixMatch[0].length);
  const lines = afterPrefix.split('\n');

  const options: string[] = [];
  let expectedLetterCode = 'a'.charCodeAt(0);
  let inOptionsBlock = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line.length === 0) {
      // Blank line: allowed BEFORE the first option (a markdown
      // paragraph break separating the question from its options),
      // but ENDS the block once we've started consuming options.
      if (inOptionsBlock) break;
      continue;
    }
    const m = OPTION_LINE.exec(rawLine);
    if (!m) {
      // Non-option, non-blank line. If we already started reading
      // options, the strip is over. If we haven't, we're in trailing
      // prose between the prefix and the options — bail; the strict
      // contract is that options follow the prefix directly (modulo
      // blank lines).
      break;
    }
    const letter = m[1]?.toLowerCase();
    const optionText = m[2]?.trim();
    if (!letter || !optionText) break;
    if (letter.charCodeAt(0) !== expectedLetterCode) {
      // Out-of-order or skipped letter — almost certainly not an
      // options strip, much more likely a citation reference like
      // "(a) ... see also (c) ...".
      break;
    }
    options.push(optionText);
    expectedLetterCode += 1;
    inOptionsBlock = true;
  }

  // A single option doesn't justify a button strip — a `(a)` on its
  // own is far more likely to be incidental prose than a real
  // options list. Require at least two for the parser to fire.
  if (options.length < 2) return null;

  return { question: questionText, options };
}
